// Helpers shared by the Clients pages: one way to write a client's name, age and address, one way to
// build the body for PUT /clients/:id, and the unsaved-changes guard for the long forms.
import { useEffect, useRef } from 'react';
import { CLIENT_TYPES, clientTypeLabel } from '../../components/ui';

// 'YYYY-MM-DD' from a DATE string or an ISO timestamp. DATE columns already arrive as plain days.
export const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

export const fullName = (c) => `${c?.first_name || ''} ${c?.last_name || ''}`.trim();

// Whole years, counted from the calendar day (no 365.25-day approximation on birthdays).
export const ageOf = (dob) => {
  const d = dayOf(dob);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, dd] = d.split('-').map(Number);
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < dd)) age -= 1;
  return Number.isFinite(age) && age >= 0 ? age : null;
};

// Days from today to a calendar day (negative = in the past). null when there is no date.
export const daysUntil = (v) => {
  const d = dayOf(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, dd] = d.split('-').map(Number);
  const t = new Date();
  const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((new Date(y, m - 1, dd) - today) / 86400000);
};

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// '21 Hill St, Penrith NSW 2750' — written once. Address autocomplete often stores the whole line
// in the street field already, so only the parts the street line doesn't mention are added.
export const formatAddress = (c) => {
  if (!c) return '';
  const street = String(c.address || '').trim();
  const low = street.toLowerCase();
  const suburb = String(c.suburb || '').trim();
  const state = String(c.state || '').trim();
  const postcode = String(c.postcode || '').trim();
  const tail = [
    suburb && !low.includes(suburb.toLowerCase()) ? suburb : '',
    state && !new RegExp(`\\b${escapeRe(state)}\\b`, 'i').test(street) ? state : '',
    postcode && !low.includes(postcode.toLowerCase()) ? postcode : '',
  ].filter(Boolean).join(' ');
  return [street, tail].filter(Boolean).join(', ');
};

// Gender is stored as free text. The form offers these; anything else is a self-description.
export const GENDERS = ['Female', 'Male', 'Non-binary'];
export const genderLabel = (g) => {
  const s = String(g || '').trim();
  if (!s) return '';
  return GENDERS.find((x) => x.toLowerCase() === s.toLowerCase()) || s.charAt(0).toUpperCase() + s.slice(1);
};

// The two stored codes for an agency-managed plan ('ndis_managed' and 'ndia_managed') are one
// option on screen. The stored code only changes when someone picks a different option.
export const typeSelectValue = (code) => (code === 'ndis_managed' ? 'ndia_managed' : code || '');
export const typeOptionsFor = (code) => {
  const v = typeSelectValue(code);
  const opts = [...CLIENT_TYPES];
  if (v && !opts.some((o) => o.value === v)) opts.push({ value: v, label: clientTypeLabel(v) });
  return opts;
};

// The primary contact: the one flagged primary, otherwise the first on file (the same rule the
// list endpoint uses).
export const primaryContactOf = (c) => {
  const list = Array.isArray(c?.contacts) ? c.contacts : [];
  return list.find((x) => x.is_primary) || list[0] || null;
};

// PUT /clients/:id REPLACES the whole record: a field missing from the body is written back empty —
// the risk alert, allergies, price book, private notes, the need-to-know list, and AI consent (as
// "off"). So every save starts from the complete record and changes only what the person edited.
export const clientPayload = (c) => ({
  firstName: c.first_name || '',
  lastName: c.last_name || '',
  dateOfBirth: dayOf(c.date_of_birth),
  gender: c.gender || '',
  sex: c.sex || '',
  nextReviewDate: dayOf(c.next_review_date),
  ndisNumber: c.ndis_number || '',
  agedCareRecipientId: c.aged_care_recipient_id || '',
  clientIdExternal: c.client_id_external || '',
  referenceNumber: c.reference_number || '',
  poNumber: c.po_number || '',
  customField: c.custom_field || '',
  clientType: c.client_type || 'ndis_managed',
  defaultPriceBookId: c.default_price_book_id || '',
  shareProgressNotes: !!c.share_progress_notes,
  generalInformation: c.general_information || '',
  privateNotes: c.private_notes || '',
  address: c.address || '',
  suburb: c.suburb || '',
  state: c.state || '',
  postcode: c.postcode || '',
  needToKnow: Array.isArray(c.need_to_know) ? c.need_to_know : [],
  usefulInformation: Array.isArray(c.useful_information) ? c.useful_information : [],
  invoiceDeliveryMethod: c.invoice_delivery_method || 'email',
  planPeriodStart: dayOf(c.plan_period_start),
  planPeriodEnd: dayOf(c.plan_period_end),
  riskAlert: c.risk_alert || '',
  riskNotification: c.risk_notification || '',
  allergies: c.allergies || '',
  countryOfBirth: c.country_of_birth || '',
  religion: c.religion || '',
  indigenousStatus: c.indigenous_status || '',
  caldBackground: c.cald_background || '',
  interpreterRequired: !!c.interpreter_required,
  preferredCarerGender: c.preferred_carer_gender || '',
  languagesSpoken: c.languages_spoken || '',
  // Sent back exactly as stored. No screen changes consent through this call.
  aiProcessingConsent: c.ai_processing_consent,
  latitude: c.latitude ?? null,
  longitude: c.longitude ?? null,
});

// CSV: quote when needed, and stop a cell that starts like a formula from running in Excel.
const csvCell = (v) => {
  let s = v == null ? '' : String(v);
  if (/^[=+@]/.test(s) || /^-[^\d.]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const downloadCsv = (filename, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// While a form has unsaved changes: the browser asks before a reload or tab close, and a click on
// any in-app link asks first (the app's router has no navigation blocker, so links are caught here).
export function useUnsavedGuard(dirty, { confirm, navigate, message = 'Your changes on this page have not been saved.' }) {
  const ref = useRef({ confirm, navigate, message });
  ref.current = { confirm, navigate, message };
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = async (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      let url;
      try { url = new URL(a.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      const { confirm: ask, navigate: go, message: msg } = ref.current;
      const ok = await ask(msg, { title: 'Leave without saving?', confirmLabel: 'Leave page', cancelLabel: 'Keep editing', danger: true });
      if (ok) go(url.pathname + url.search + url.hash);
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);
}
